import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { fetchWithAuth } from '../../services/api';
import ProfileDeleteModal from '../../components/ProfileDeleteModal';

const AdminProfilesList = () => {
    const { t } = useTranslation();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [deletedFilter, setDeletedFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const navigate = useNavigate();

    // Modal states for delete
    const [deletingProfile, setDeletingProfile] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    useEffect(() => {
        const fetchProfiles = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const role = localStorage.getItem('userRole');

                if (!token || role !== 'ADMIN') {
                    navigate('/');
                    return;
                }

                const data = await fetchWithAuth('/admin/profiles');
                setProfiles(data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching profiles:', err);
                setError('Failed to fetch profiles');
                setLoading(false);
            }
        };

        fetchProfiles();
    }, [navigate]);

    const calculateLifespan = (birthDate, deathDate) => {
        if (!birthDate || !deathDate) return '-';
        // Parse DD.MM.YYYY format
        const birthParts = birthDate.split('.');
        const deathParts = deathDate.split('.');
        if (birthParts.length !== 3 || deathParts.length !== 3) return '-';
        const birth = new Date(birthParts[2], birthParts[1] - 1, birthParts[0]);
        const death = new Date(deathParts[2], deathParts[1] - 1, deathParts[0]);
        if (isNaN(birth.getTime()) || isNaN(death.getTime())) return '-';
        const years = Math.floor((death - birth) / (365.25 * 24 * 60 * 60 * 1000));
        return `${years} years`;
    };

    const handleEditProfile = async (profile, event) => {
        event.stopPropagation();
        navigate(`/mk/edit-profile/${profile.stringId || profile.id}`);
    };

    const handleDeleteProfile = (profile, event) => {
        event.stopPropagation();
        setDeletingProfile(profile);
        setIsDeleteModalOpen(true);
    };

    const handleProfileDeleted = (profileId) => {
        setProfiles(prevProfiles =>
            prevProfiles.map(profile => profile.id === profileId
                ? { ...profile, deleted: true, deletedAt: new Date().toISOString() }
                : profile)
        );
    };

    const visibleProfiles = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        return profiles
            .filter(profile => deletedFilter === 'all'
                || (deletedFilter === 'deleted' ? profile.deleted : !profile.deleted))
            .filter(profile => !normalizedSearch
                || profile.name?.toLocaleLowerCase().includes(normalizedSearch)
                || profile.userFullName?.toLocaleLowerCase().includes(normalizedSearch))
            .sort((left, right) => {
                if (deletedFilter === 'all' && Boolean(left.deleted) !== Boolean(right.deleted)) {
                    return left.deleted ? 1 : -1;
                }
                if (sortBy === 'name') return (left.name || '').localeCompare(right.name || '');
                const direction = sortBy === 'oldest' ? 1 : -1;
                return direction * (new Date(left.createdAt || 0) - new Date(right.createdAt || 0));
            });
    }, [profiles, search, deletedFilter, sortBy]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-500 text-lg">{error}</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2
                        className="text-2xl font-semibold text-gray-800"
                        data-testid="admin-profile-list-title"
                    >
                        {t('admin.profiles.title', 'Profiles Management')}
                    </h2>
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(240px,1fr)_180px_180px]">
                        <label className="relative">
                            <span className="sr-only">{t('common.search', 'Search')}</span>
                            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={t('admin.profiles.searchPlaceholder', 'Search by profile or owner name')}
                                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                        </label>
                        <select value={deletedFilter} onChange={(event) => setDeletedFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            <option value="all">{t('admin.profiles.filters.all', 'All profiles')}</option>
                            <option value="active">{t('admin.profiles.filters.active', 'Active only')}</option>
                            <option value="deleted">{t('admin.profiles.filters.deleted', 'Deleted only')}</option>
                        </select>
                        <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                            <option value="newest">{t('common.newestFirst', 'Newest first')}</option>
                            <option value="oldest">{t('common.oldestFirst', 'Oldest first')}</option>
                            <option value="name">{t('common.nameAZ', 'Name A–Z')}</option>
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                    {t('admin.profiles.table.icon', 'Icon')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.name', 'Name')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.born', 'Born')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.died', 'Died')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.owner', 'Owner')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.visits', 'Visits')}
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.deleted', 'Deleted')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('admin.profiles.table.actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {visibleProfiles.map((profile) => {
                                const profileImageUrl = profile.uploadedImages && profile.uploadedImages.length > 0
                                    ? profile.uploadedImages[0].url
                                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || 'P')}&background=random&color=fff&size=64`;

                                return (
                                    <tr
                                        key={profile.stringId || profile.id}
                                        className={`${profile.deleted ? 'bg-gray-100 text-gray-500' : 'hover:bg-gray-50'} transition-colors duration-150`}
                                        data-testid={`admin-profile-item-${profile.stringId || profile.id}`}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <img
                                                src={profileImageUrl}
                                                alt={profile.name || 'Profile icon'}
                                                className="h-10 w-10 rounded-full object-cover bg-gray-200"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {profile.deleted ? (
                                                <span className="text-sm font-medium text-gray-500" data-testid="admin-profile-item-name">{profile.name}</span>
                                            ) : (
                                                <a href={`/profile/${profile.stringId || profile.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline" target="_blank" rel="noopener noreferrer" data-testid="admin-profile-item-name">
                                                    {profile.name}
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {profile.birthDate} {profile.birthPlace && `(${profile.birthPlace})`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {profile.deathDate} {profile.deathPlace && `(${profile.deathPlace})`}
                                                {profile.birthDate && profile.deathDate && (
                                                    <div className="text-xs text-gray-500">
                                                        {calculateLifespan(profile.birthDate, profile.deathDate)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {profile.userFullName || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-center">
                                                <span className="text-lg font-bold text-blue-600">
                                                    {profile.linkOpensCount || 0}
                                                </span>
                                                {profile.lastVisitedAt && (
                                                    <div className="text-xs text-gray-400">
                                                        Last: {new Date(profile.lastVisitedAt).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {profile.deleted
                                                ? <CheckIcon className="mx-auto h-5 w-5 text-red-600" aria-label="Deleted" />
                                                : <XMarkIcon className="mx-auto h-5 w-5 text-gray-400" aria-label="Not deleted" />}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end items-center space-x-2">
                                                {!profile.deleted && (<>
                                                <a
                                                    href={`/profile/${profile.stringId || profile.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-indigo-600 hover:text-indigo-900 p-1"
                                                    title={t('common.view', 'View')}
                                                    data-testid={`admin-view-profile-${profile.stringId || profile.id}`}
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                </a>
                                                <button
                                                    onClick={(e) => handleEditProfile(profile, e)}
                                                    className="text-blue-600 hover:text-blue-900 p-1"
                                                    title={t('admin.profiles.edit', 'Edit Profile')}
                                                    data-testid={`admin-edit-profile-${profile.stringId || profile.id}`}
                                                >
                                                    <PencilSquareIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteProfile(profile, e)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title={t('admin.profiles.delete', 'Delete Profile')}
                                                    data-testid={`admin-delete-profile-${profile.stringId || profile.id}`}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                                </>)}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {visibleProfiles.length === 0 && (
                        <div className="px-6 py-12 text-center text-sm text-gray-500">
                            {t('admin.profiles.noMatchingProfiles', 'No profiles match these filters.')}
                        </div>
                    )}
                </div>
            </div>

            {/* Profile Delete Modal */}
            <ProfileDeleteModal
                profile={deletingProfile ? { ...deletingProfile, isAdmin: true } : null}
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingProfile(null);
                }}
                onDelete={handleProfileDeleted}
            />
        </div>
    );
};

export default AdminProfilesList;
